import { API_DOMAIN } from '../../../components/constants';
import { apiPost, apiDelete } from '../../../components/qv4/models/shared/apiClient';

export const LOGO_MAX_BYTES = 5 * 1024 * 1024;
export function logoFileError(file) {
  if (!file || !['image/png', 'image/webp', 'image/jpeg'].includes(file.type) || !/\.(png|webp|jpe?g)$/i.test(file.name)) return 'Choose a PNG, WebP or JPEG image. SVG files cannot be used.';
  if (file.size > LOGO_MAX_BYTES) return 'Choose an image no larger than 5 MB.';
  return null;
}
export function makeLogoUrl(value) {
  const url = typeof value === 'string' ? value : value?.make_logo_url ?? value?.logo?.url;
  if (!url) return null;
  try { const resolved = new URL(url, API_DOMAIN); return ['http:', 'https:'].includes(resolved.protocol) ? resolved.href : null; } catch { return null; }
}
const errors = {
  LOGO_FILE_INVALID: 'This image could not be opened. Choose another image.',
  LOGO_TYPE_NOT_ALLOWED: 'Choose a PNG, WebP or JPEG image. SVG files cannot be used.',
  LOGO_TOO_LARGE: 'Choose an image up to 5 MB and 6000 pixels on each side, without animation.',
  LOGO_CROP_INVALID: 'Adjust the frame so it includes part of the image.',
  INVALID_INPUT: 'Check the image and crop settings, then try again.',
  VERSION_CONFLICT: 'Someone else changed this make. Close this window and reopen it to use the latest logo.',
  MAKE_NOT_FOUND: 'This make could not be found. Reload the makes list.',
  LOGO_PREVIEW_RATE_LIMITED: 'Too many previews. Wait a minute, then try again.',
  LOGO_STORAGE_FAILED: 'The logo could not be stored. Please try again.',
};
export const logoErrorCode = error => error?.response?.data?.code || error?.code;
export const logoErrorMessage = error => errors[logoErrorCode(error)] || (error?.response?.status === 403 ? 'You do not have permission to manage make logos.' : 'The logo could not be processed. Please try again.');
function unwrap(response) {
  if (!response.data?.success) throw Object.assign(new Error('Logo request failed.'), { code: response.data?.code });
  return response.data.data;
}
export function logoFormData({ file, crop, whiteToTransparent, version }) {
  const validation = logoFileError(file); if (validation) throw new Error(validation);
  const data = new FormData(); data.append('file', file);
  if (crop) {
    const rectangle = Object.fromEntries(['x', 'y', 'width', 'height'].map(key => [key, Math.round(crop[key])]));
    if (Object.values(rectangle).some(value => !Number.isFinite(value)) || rectangle.x < 0 || rectangle.y < 0 || rectangle.width <= 0 || rectangle.height <= 0) throw Object.assign(new Error('Invalid crop'), { code: 'LOGO_CROP_INVALID' });
    data.append('crop', JSON.stringify(rectangle));
  }
  data.append('white_to_transparent', String(whiteToTransparent === true));
  if (version != null) {
    if (!Number.isSafeInteger(version) || version < 0) throw new Error('Reload the make before saving.');
    data.append('expected_version', String(version));
  }
  return data;
}
export const previewMakeLogo = (id, values, signal) => apiPost(`/vehicle-catalogue-v1/makes/${id}/logo/preview`, logoFormData(values), { signal }).then(unwrap);
export const saveMakeLogo = (id, values) => apiPost(`/vehicle-catalogue-v1/makes/${id}/logo`, logoFormData(values)).then(unwrap);
export const removeMakeLogo = (id, version) => apiDelete(`/vehicle-catalogue-v1/makes/${id}/logo`, { expected_version: version }).then(unwrap);
export function logoSourceLabel(logo) {
  if (logo?.source === 'website_dataset') return 'From the website set';
  if (logo?.source === 'cdn') return 'From the CDN';
  if (logo?.source !== 'upload') return '';
  const date = logo.updated_at && new Date(logo.updated_at);
  // The published resource does not expose an uploader name. Do not guess it.
  return date && Number.isFinite(date.getTime()) ? `Uploaded · ${new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Dubai', dateStyle: 'medium' }).format(date)}` : 'Uploaded';
}
export function fitLogoZoom(media, frame = { width: 450, height: 300 }) {
  const fit = Math.min(frame.width / media.naturalWidth, frame.height / media.naturalHeight);
  return Math.min(frame.width * 0.84 / (media.naturalWidth * fit), frame.height * 0.76 / (media.naturalHeight * fit));
}

// Fitting the whole image can leave transparent frame space outside its edges.
// The server crops real source pixels, then centres that crop on its own canvas.
export function sourceLogoCrop(rect, media) {
  if (!rect || !media || ![rect.x, rect.y, rect.width, rect.height, media.naturalWidth, media.naturalHeight].every(Number.isFinite)) return null;
  const x = Math.max(0, Math.min(media.naturalWidth, Math.round(rect.x)));
  const y = Math.max(0, Math.min(media.naturalHeight, Math.round(rect.y)));
  const right = Math.max(0, Math.min(media.naturalWidth, Math.round(rect.x + rect.width)));
  const bottom = Math.max(0, Math.min(media.naturalHeight, Math.round(rect.y + rect.height)));
  return right > x && bottom > y ? { x, y, width: right - x, height: bottom - y } : null;
}
